/* header.js — interactions de l'en-tête « Tiitoune Aurum ».
   État de défilement, barre de recherche dépliante, menus déroulants
   (clic / tactile, en plus du survol CSS) et tiroir mobile avec piège de
   focus + verrou de défilement. Vanilla JS, aucune dépendance.
   Ré-initialisable via shopify:section:load (éditeur de thème). */
(function () {
  'use strict';

  /* Garde anti double-exécution — l'éditeur de thème ré-exécute les scripts
     de section à chaque modification ; on ne s'attache qu'une seule fois. */
  if (window.__aurumHeader) return;
  window.__aurumHeader = true;

  var SCROLL_THRESHOLD = 24; /* px de défilement avant le liseré or */

  /* Verrou de défilement global à compteur de références — partagé entre le
     tiroir mobile du header et le tiroir panier. Défini à l'identique (garde
     window.*) dans header.js et cart.js : le premier chargé gagne, l'ordre
     de chargement des deux scripts est donc indifférent. */
  window.AurumScrollLock = window.AurumScrollLock || (function () {
    var count = 0;
    return {
      acquire: function () {
        count += 1;
        document.documentElement.style.overflow = 'hidden';
      },
      release: function () {
        count = Math.max(0, count - 1);
        if (count === 0) document.documentElement.style.overflow = '';
      }
    };
  })();

  /* ------------------------------------------------------------------
     État de défilement — listener global unique, throttlé par rAF.
     On re-cherche [data-header] à chaque frame : l'élément peut être
     remplacé par l'éditeur de thème sans qu'on ait à re-binder.
     ------------------------------------------------------------------ */
  var scrollTicking = false;

  function updateScrollState() {
    scrollTicking = false;
    var header = document.querySelector('[data-header]');
    if (!header) return;
    header.classList.toggle('is-scrolled', window.scrollY > SCROLL_THRESHOLD);
  }

  function onScroll() {
    if (scrollTicking) return;
    scrollTicking = true;
    window.requestAnimationFrame(updateScrollState);
  }

  window.addEventListener('scroll', onScroll, { passive: true });

  /* ------------------------------------------------------------------
     Utilitaires
     ------------------------------------------------------------------ */

  /* Éléments focusables visibles à l'intérieur d'un conteneur.
     NB : helper de piège de focus dupliqué dans cart.js — toute évolution
     doit être reportée dans les deux fichiers en parallèle. */
  function getFocusables(container) {
    var selector = 'a[href], button:not([disabled]), input:not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])';
    return Array.prototype.filter.call(
      container.querySelectorAll(selector),
      function (el) {
        return el.getClientRects().length > 0;
      }
    );
  }

  /* Ferme tous les menus déroulants ouverts (du document entier :
     un seul listener global suffit, même après re-rendu de section) */
  function closeAllDropdowns() {
    var openItems = document.querySelectorAll('[data-dropdown].is-open');
    Array.prototype.forEach.call(openItems, function (item) {
      item.classList.remove('is-open');
      var toggle = item.querySelector('[data-dropdown-toggle]');
      if (toggle) toggle.setAttribute('aria-expanded', 'false');
    });
  }

  /* Clic en dehors d'un menu déroulant → fermeture (listener global unique) */
  document.addEventListener('click', function (event) {
    if (!event.target.closest('[data-dropdown]')) closeAllDropdowns();
  });

  /* ------------------------------------------------------------------
     Initialisation d'une instance de la section header
     ------------------------------------------------------------------ */
  function initHeader(scope) {
    var header = scope.querySelector('[data-header]');
    if (!header || header.dataset.aurumHeaderInit === '1') return;
    header.dataset.aurumHeaderInit = '1'; /* garde anti double-binding */

    /* Racine commune : le wrapper de section si présent, sinon le scope
       (le tiroir est un frère du <header>, pas un descendant) */
    var root = header.closest('.shopify-section') || scope;

    /* État initial du liseré (cas d'une page déjà défilée au chargement) */
    updateScrollState();

    /* ---------------- Menus déroulants (clic / tactile) ---------------- */
    var dropdownItems = root.querySelectorAll('[data-dropdown]');
    Array.prototype.forEach.call(dropdownItems, function (item) {
      var toggle = item.querySelector('[data-dropdown-toggle]');
      if (!toggle) return;
      toggle.addEventListener('click', function () {
        var wasOpen = item.classList.contains('is-open');
        closeAllDropdowns();
        if (!wasOpen) {
          item.classList.add('is-open');
          toggle.setAttribute('aria-expanded', 'true');
        }
      });
    });

    /* ---------------- Barre de recherche dépliante ---------------- */
    var searchToggle = root.querySelector('[data-search-toggle]');
    var searchPanel = root.querySelector('[data-search-panel]');

    function openSearch() {
      searchPanel.hidden = false;
      searchPanel.classList.add('is-open');
      searchToggle.setAttribute('aria-expanded', 'true');
      var input = searchPanel.querySelector('input[type="search"]');
      if (input) input.focus();
    }

    /* Pas de fermeture sur focusout/clic exterieur : choix délibéré — un clic sur
       un résultat de recherche prédictive doit aboutir avant toute fermeture. */
    function closeSearch(refocus) {
      searchPanel.classList.remove('is-open');
      searchPanel.hidden = true;
      searchToggle.setAttribute('aria-expanded', 'false');
      if (refocus) searchToggle.focus();
    }

    if (searchToggle && searchPanel) {
      searchToggle.addEventListener('click', function () {
        if (searchPanel.hidden) {
          openSearch();
        } else {
          closeSearch(false);
        }
      });

      var searchClose = searchPanel.querySelector('[data-search-close]');
      if (searchClose) {
        searchClose.addEventListener('click', function () {
          closeSearch(true);
        });
      }

      /* Échap dans la barre de recherche → fermeture + retour du focus */
      searchPanel.addEventListener('keydown', function (event) {
        if (event.key === 'Escape') closeSearch(true);
      });
    }

    /* ---------------- Tiroir mobile ---------------- */
    var burger = root.querySelector('[data-drawer-toggle]');
    var drawer = root.querySelector('[data-drawer]');

    if (burger && drawer) {
      var drawerHideTimer = null;

      function openDrawer() {
        /* Annule un masquage différé si on rouvre pendant la fermeture */
        if (drawerHideTimer) {
          window.clearTimeout(drawerHideTimer);
          drawerHideTimer = null;
        }
        drawer.hidden = false;
        /* Une frame pour que la transition d'opacité parte de 0 */
        window.requestAnimationFrame(function () {
          drawer.classList.add('is-open');
        });
        burger.setAttribute('aria-expanded', 'true');
        window.AurumScrollLock.acquire(); /* verrou scroll partagé (refcount) */
        var closeBtn = drawer.querySelector('[data-drawer-close]');
        if (closeBtn) closeBtn.focus();
      }

      /* Fonction de fermeture partagée — utilisée par :
         le bouton ✕, la touche Échap, le listener matchMedia et
         shopify:section:unload (libération du verrou de défilement). */
      function closeDrawer() {
        drawer.classList.remove('is-open');
        burger.setAttribute('aria-expanded', 'false');
        window.AurumScrollLock.release();
        /* On laisse la transition d'opacité (300 ms) se terminer */
        drawerHideTimer = window.setTimeout(function () {
          drawer.hidden = true;
          drawerHideTimer = null;
        }, 320);
        burger.focus(); /* le focus revient sur le burger */
      }

      burger.addEventListener('click', function () {
        if (drawer.hidden) {
          openDrawer();
        } else {
          closeDrawer();
        }
      });

      /* Bouton ✕ — utilise closeDrawer partagée */
      var drawerClose = drawer.querySelector('[data-drawer-close]');
      if (drawerClose) {
        drawerClose.addEventListener('click', closeDrawer);
      }

      /* Piège de focus + Échap, à l'intérieur du tiroir */
      drawer.addEventListener('keydown', function (event) {
        /* Échap — utilise closeDrawer partagée */
        if (event.key === 'Escape') {
          closeDrawer();
          return;
        }
        if (event.key !== 'Tab') return;
        var focusables = getFocusables(drawer);
        if (focusables.length === 0) return;
        var first = focusables[0];
        var last = focusables[focusables.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      });

      /* Rotation tablette : si l'écran passe en ≥ 990 px pendant que le
         tiroir est ouvert, on le ferme et on libère le verrou de défilement. */
      var mqDesktop = window.matchMedia('(min-width: 990px)');
      function onDesktopBreakpoint(mq) {
        if (mq.matches && !drawer.hidden) {
          closeDrawer();
        }
      }
      /* addEventListener avec objet (plus récent) ; fallback addListener */
      if (mqDesktop.addEventListener) {
        mqDesktop.addEventListener('change', onDesktopBreakpoint);
      } else {
        mqDesktop.addListener(onDesktopBreakpoint);
      }
    }

    /* ---------------- Échap global dans l'en-tête ---------------- */
    root.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape') return;
      /* Le focus revient sur le déclencheur du menu déroulant ouvert */
      var openItem = root.querySelector('[data-dropdown].is-open');
      closeAllDropdowns();
      if (openItem) {
        var openToggle = openItem.querySelector('[data-dropdown-toggle]');
        if (openToggle) openToggle.focus();
      }
    });

    /* NB : le lien panier ([data-cart-drawer-toggle]) n'est volontairement
       pas intercepté ici — il navigue vers /cart tant que cart.js (tâche 10)
       n'a pas branché le tiroir panier. */
  }

  /* ------------------------------------------------------------------
     Démarrage + ré-initialisation dans l'éditeur de thème
     ------------------------------------------------------------------ */
  initHeader(document);

  document.addEventListener('shopify:section:load', function (event) {
    initHeader(event.target);
  });

  /* Si la section est déchargée avec le tiroir ouvert, on libère le scroll.
     Le tiroir sera détruit par l'éditeur ; on se contente de nettoyer le DOM. */
  document.addEventListener('shopify:section:unload', function (event) {
    var unloadedDrawer = event.target.querySelector('[data-drawer]');
    if (unloadedDrawer && !unloadedDrawer.hidden) {
      window.AurumScrollLock.release(); /* libère la référence prise à l'ouverture */
    }
  });
})();
